// La barra de entrada del chat: adjuntos enganchados, textarea que crece, el menú "+",
// el selector de Inteligencia y el botón de enviar/detener.
//
// Compartida entre el panel lateral y la pantalla dedicada — no hay una segunda copia.
// El look es el del composer de "Asistente v2"
// (ADR-2026-09-12-el-agente-toma-el-diseno-del-asistente-v2): caja redonda con un solo
// trazo gris que al enfocar se vuelve un aro de gradiente que gira
// (`.composer-borde-vivo`, index.css), el "+" con rótulo "Agregar" en la pantalla
// ancha, el mic siempre a mano (lo dictado se SUMA a lo escrito) y el botón de enviar
// siempre presente, en el gradiente azul de generar, atenuado hasta la primera letra.
//
// Los adjuntos son los del MOTOR (`ChatAttachment`): imagen subida, archivo de texto,
// producto o modelo — ADR-2026-09-08-el-chat-toma-el-motor-del-asistente. Desde acá se
// enganchan archivos o fotos; producto y modelo los engancha el agente con sus tarjetas.
//
// Y el LOTE DE FOTOS (ADR-2026-09-08-el-chat-trabaja-en-lote,
// ADR-2026-09-08-agregar-productos-es-una-herramienta): muchas fotos de productos que NO
// son referencias de una imagen sino productos por cargar. Entra por la MISMA "Agregar
// archivos o fotos" con `UMBRAL_LOTE_FOTOS` o más imágenes (no hay una opción aparte:
// cargar productos es una capacidad del agente). Es UN chip, no viaja al modelo, y el
// mensaje se puede mandar sin texto: va un pedido por defecto.

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import {
  ArrowUp,
  FileText,
  Image as ImageIcon,
  Loader2,
  Mic,
  Package,
  FileType2,
  Palette,
  PersonStanding,
  Table2,
  Plus,
  Square,
  User,
  X, Film,
  Clock,
  Paperclip,
} from 'lucide-react';
import DictadoVoz, { dictadoDisponible } from '../../pages/v2/modes/asistente/DictadoVoz';
import type { Intelligence } from '../../pages/v2/agentModels';
import type { ChatAttachment } from '../../pages/v2/agentAttachments';
import { fotosDelMoodboard } from '../../pages/v2/etiquetaDeAdjunto';
import {
  archivosDelPortapapeles,
  archivosArrastrados,
  traeArchivos,
  ACEPTA_ADJUNTOS,
  esLoteDeFotos,
  formatearPeso,
  lugarLibreAdjuntos,
  MAX_FOTOS_LOTE,
  mensajeLimiteAdjuntos,
  mensajeLoteFotos, formatearDuracion,
  archivosEnganchados,
} from '../adjuntosChatAtlas';
import type { InterruptoresChat } from '../tipos';
import { MenuMas } from './MenuMas';
import type { AccionMenuMas, VistaDelMenu } from './menuMasItems';
import { prepararAdjunto } from './prepararAdjunto';
import { hayLugarEnCola, MAX_COLA, type MensajeEnCola } from '../colaDeMensajes';
import { ATRIBUTO_CAJA, ATRIBUTO_CHIP } from './vueloAlComposer';
import type { PreferenciasDeGeneracion } from '../preferenciasDeGeneracion';
import { PastillasDeContenido } from './PastillasDeContenido';
import { SelectorInteligencia } from './SelectorInteligencia';
import { disposicionDeLaFila } from './filaDeEntrada';
import { useMenusDeTexto } from './useMenusDeTexto';
import { GRADIENTE_ENVIAR } from './estilosAgente';
import type { ProveedorDeImagen } from '../../lib/proveedoresDeImagen';
import { palabraDeModeloActual } from '../../lib/nombreDeModelo';

/** La tipografía y el padding del textarea, compartidos con la capa espejo que resalta
 *  `#colores` y `@menciones` detrás (tienen que calcar glifo por glifo). El `pt` va aparte
 *  porque depende de si hay tarjeta arriba. */
const CLASES_DEL_TEXTO = 'px-3 pb-2 text-[15px] leading-snug text-[#02062B]/[0.9] placeholder:text-[#02062B]/[0.38]';

/** Hasta dónde crece el textarea antes de scrollear adentro. Mismo número que el
 *  composer de "Crear desde cero", para que escribir se sienta igual en los dos lados. */
const ALTO_MAX_TEXTAREA = 240;

/**
 * Las tres pantallas del menú del "+" que NO se listan cuando la caja lleva la fila de
 * pastillas — ADR-2026-09-21-todo-lo-del-menu-que-no-adjunta-sale-a-la-fila-de-pastillas.
 * Son todas las que no adjuntan: lo que queda en el menú es de dónde sale lo que se
 * engancha (archivos y los catálogos de la marca) más Conectores, que va a ser otra fuente.
 *
 * Ya no se esconden todas por el mismo motivo, y por eso el nombre dejó de decir "en las
 * pastillas" (ADR-2026-09-24-el-motor-y-las-herramientas-viven-en-configuracion):
 *
 *   • `preferencias` — sí está en la fila de pastillas, ahí al lado.
 *   • `herramientas` y `modelos` — se fueron a Configuración → Funciones avanzadas. En una
 *     caja con pastillas no se ofrecen acá porque el lugar donde se tocan ahora es ese.
 *
 * Desde ADR-2026-09-25-el-mas-del-agente-abre-el-selector-de-fuentes la caja del chat
 * también lleva pastillas y su "+" abre el selector de fuentes, así que hoy ninguna caja
 * monta el menú del "+": esto queda para una caja que lo use con pastillas.
 */
const VISTAS_FUERA_DEL_MENU: VistaDelMenu[] = ['herramientas', 'preferencias', 'modelos'];

/** Lo que la barra muestra de un lote enganchado. */
export interface LoteEnganchado {
  id: string;
  cantidad: number;
}

/** Lo que se manda cuando hay un lote enganchado y el usuario no escribió nada. */
export const PEDIDO_POR_DEFECTO_LOTE = 'Organizá estas fotos en productos y agregalas a mi catálogo.';

interface Props {
  valor: string;
  onCambiarValor: (texto: string) => void;
  onEnviar: (texto: string) => void;
  streaming: boolean;
  onDetener: () => void;
  /** La cola (ADR-2026-09-13-la-cola-de-mensajes-del-agente): mientras responde, Enviar
   *  encola en vez de mandar; los encolados se muestran arriba de la caja. */
  cola: MensajeEnCola[];
  onEncolar: (texto: string) => boolean;
  onQuitarDeCola: (indice: number) => void;
  interruptores: InterruptoresChat;
  onCambiarInterruptor: (clave: keyof InterruptoresChat, valor: boolean) => void;
  /** Formato y calidad por defecto (la pantalla "Preferencias de contenido" del menú "+"). */
  preferencias: PreferenciasDeGeneracion;
  onCambiarPreferencia: <K extends keyof PreferenciasDeGeneracion>(
    clave: K,
    valor: PreferenciasDeGeneracion[K],
  ) => void;
  adjuntos: ChatAttachment[];
  /**
   * Adjuntos que NO se dibujan como chip (otro componente ya los muestra, como las tarjetas
   * de Crear). Siguen contando para la `@` y para el tope: sólo se esconde su miniatura.
   */
  ocultarChip?: (a: ChatAttachment) => boolean;
  onAgregarAdjunto: (a: ChatAttachment) => void;
  onQuitarAdjunto: (id: string) => void;
  /** Lo seleccionado en el lienzo que todavía no está adjunto: se dibuja apagado y sin X;
   *  escribir lo adjunta (ADR-2026-09-24-la-seleccion-del-lienzo-se-adjunta-al-escribir). */
  adjuntosTentativos?: ChatAttachment[];
  /** El lote de fotos enganchado, si hay. */
  loteFotos?: LoteEnganchado | null;
  onAdjuntarLote?: (archivos: File[]) => void;
  onQuitarLote?: () => void;
  /** Abrir uno de los selectores del catálogo de la marca (producto, modelo, pose,
   *  fondo, imagen ya generada) — ADR-2026-09-08-adjuntar-desde-el-catalogo-de-la-marca. */
  onAbrirSelector?: (accion: Exclude<AccionMenuMas, 'archivos'>) => void;
  /** Empresa: la necesita la subida de imágenes. `null` mientras carga la cuenta. */
  companyId: string | null;
  inteligencia: Intelligence;
  onCambiarInteligencia: (nivel: Intelligence) => void;
  /** ¿El plan deja subir la inteligencia? Con `false` el selector dibuja Media y Alta con
   *  candado (ADR-2026-09-12-la-inteligencia-del-chat-es-de-pro-para-arriba). */
  puedeSubirInteligencia: boolean;
  /**
   * El motor de imagen elegido. `null` = Atlas elige (el default).
   *
   * Ya no se dibuja acá: era una tuerca al lado del micrófono para cuentas dev y pasó a ser
   * "Opciones de modelo avanzadas" adentro del menú del "+"
   * (ADR-2026-09-14-el-modelo-de-imagen-lo-elige-el-usuario). La barra sólo lo pasa.
   */
  modeloDeImagen: ProveedorDeImagen | null;
  onCambiarModeloDeImagen: (proveedor: ProveedorDeImagen | null) => void;
  /** Foco automático al montar (el panel lo pide; la pantalla también). */
  autoFoco?: boolean;
  placeholder?: string;
  /** `amplio`: la caja mide 200px de alto mínimo (el arranque de la pantalla dedicada,
   *  donde el composer está en el medio y con aire de párrafo). `normal`: crece con el
   *  texto, como al pie de una conversación. */
  alto?: 'normal' | 'amplio';
  /** El "+" con el rótulo "Agregar" al lado, en todos los tamaños: la pantalla del
   *  Agente lo pide (también en el teléfono, donde la fila tiene lugar —
   *  ADR-2026-09-25-agregar-con-rotulo-y-la-hoja-entera-en-el-telefono).
   *
   *  `'si-entra'`: el panel lateral y la conversación con el lienzo abierto en desktop,
   *  que son más angostos. La barra mide su fila y cede primero la palabra de las
   *  pastillas y después el rótulo (`disposicionDeLaFila`,
   *  ADR-2026-09-25-agregar-con-rotulo-en-el-panel-y-con-el-lienzo). */
  rotuloAgregar?: boolean | 'si-entra';
  /**
   * Dibujar el botón "+" y su menú — ADR-2026-09-21-la-caja-de-la-home-no-lleva-el-mas.
   *
   * La caja de la home lo apaga: con la tarjeta de fuentes puesta a la izquierda, el "+"
   * quedaba al lado ofreciendo lo mismo (y, con las pastillas de contenido, ya no le
   * quedaba nada propio). El panel lateral y la pantalla del chat lo siguen llevando: allá
   * no hay tarjeta.
   *
   * El `<input type=file>` NO se va con el botón: sigue vivo para pegar y arrastrar sobre
   * la caja, que entran por el mismo camino.
   */
  conBotonMas?: boolean;
  /**
   * Si viene, el "+" abre ESTO (el selector de fuentes con pestañas, `ModalDeFuentes`) en
   * vez del menú del "+", que entonces no se monta
   * (ADR-2026-09-25-el-mas-del-agente-abre-el-selector-de-fuentes). Lo pasa el chat; sin
   * él, el "+" abre su menú como siempre.
   */
  onAbrirFuentes?: () => void;
  /** Panel lateral (angosto) o pantalla dedicada: el menú del "+" mide lo que la caja y
   *  pone sus cards de a 3 por fila en la pantalla y de a 2 en el panel
   *  (ADR-2026-09-12-el-menu-del-mas-al-ancho-de-la-caja-con-cards). */
  variante?: 'panel' | 'pantalla';
  /** Avisa cuando el textarea gana o pierde el foco: el arranque en mobile esconde el
   *  título y muestra las sugerencias mientras se escribe. */
  onEnfoque?: (enfocado: boolean) => void;
  /** Un pedido de foco desde afuera: cada vez que el número cambia, el textarea toma el
   *  foco con el cursor al final. Lo usan las sugerencias del arranque, que completan la
   *  caja sin mandar: el usuario tiene que quedar escribiendo sobre lo completado
   *  (ADR-2026-09-12-las-sugerencias-completan-la-caja). Es un contador y no un booleano
   *  para que la misma sugerencia dos veces también enfoque. */
  pedidoDeFoco?: number;
  /**
   * Una tarjeta ADENTRO de la caja, ARRIBA de todo: su propia fila, antes del texto y con
   * los chips de lo enganchado A SU DERECHA —
   * ADR-2026-09-21-la-caja-de-la-home-abre-el-selector-de-fuentes, movida de la
   * izquierda del textarea a su propia fila por ADR-2026-09-22-la-tarjeta-va-arriba-del-texto
   * y acompañada por los chips en ADR-2026-09-22-lo-enganchado-va-al-lado-de-la-tarjeta.
   *
   * Hoy la usa una sola caja, la de la home, con "Agregar producto". Entra como nodo y
   * no como un `onAgregarProducto?: () => void` porque la barra no tiene por qué saber
   * qué es: lo que le toca es el LUGAR (su fila, la primera de la caja), y el dibujo y lo
   * que abre son de quien la manda.
   *
   * Sin tarjeta la caja queda exactamente como estaba, con el texto como lo primero que
   * se ve. Es lo que ven el panel lateral y la pantalla del chat.
   */
  tarjetaDeFuentes?: ReactNode;
  /**
   * Una fila ADENTRO de la caja, arriba de todo y a todo el ancho, antes de la tarjeta y del
   * texto. La usa el panel del Asistente de Crear para sus tarjetas de Producto, Avatar y
   * Referencia (ADR-2026-10-13-las-tarjetas-de-crear-en-el-panel-del-asistente-2). Sin ella
   * la caja queda como estaba. No se dibuja mientras se dicta.
   */
  encabezado?: ReactNode;
  /**
   * La fila de pastillas —switch Imagen/Video, Formato, Calidad, Duración— adentro de la
   * caja, entre el texto y la fila del "+"
   * (ADR-2026-09-21-las-preferencias-salen-a-la-caja-como-pastillas).
   *
   * La pide la caja de la HOME y, desde ADR-2026-09-25-el-mas-del-agente-abre-el-selector-de-fuentes,
   * también la del chat (panel lateral y pantalla dedicada). Hoy la fila es la
   * Inteligencia y el engranaje, en el mismo renglón que el "+", el mic y el enviar: no
   * suma un renglón a la caja, que era lo que la dejaba afuera del chat.
   *
   * Con las pastillas puestas, el menú del "+" ESCONDE sus TRES pantallas —Herramientas,
   * Preferencias de contenido y Opciones de modelo avanzadas— y la fila de abajo no dibuja
   * el selector de Inteligencia: todo eso está en las pastillas
   * (ADR-2026-09-21-todo-lo-del-menu-que-no-adjunta-sale-a-la-fila-de-pastillas). Son el
   * mismo estado, y mostrarlo dos veces en la misma caja es la forma de que se
   * desincronicen en la cabeza del usuario.
   */
  pastillasDeContenido?: boolean;
}


/**
 * EL ALTO DE LA FILA DE FUENTES — ADR-2026-09-22-los-chips-miden-lo-mismo-que-la-tarjeta.
 *
 * La tarjeta "Agregar producto" es un cuadrado de este lado y los chips de lo enganchado
 * miden ESTE alto, así la fila es una sola banda pareja. El número vive acá, donde se
 * arma la fila, y lo importa la tarjeta (`CajaDelAgenteHome`): con una copia en cada
 * archivo, mover uno dejaba al otro desalineado y nadie se enteraba hasta verlo.
 *
 * Dos medidas porque la caja tiene dos anchos: la pantalla de escritorio y el teléfono
 * (`variante`), donde 92px de banda se comen el hero.
 */
export const ALTO_DE_LA_FILA_DE_FUENTES = { pantalla: 92, panel: 68 } as const;

export const ChipAdjunto: React.FC<{
  adjunto: ChatAttachment;
  onQuitar?: () => void;
  /** Seleccionada en el lienzo pero no adjunta todavía: apagada, sin X
   *  (ADR-2026-09-24-la-seleccion-del-lienzo-se-adjunta-al-escribir). */
  tentativo?: boolean;
  /**
   * Alto fijo en píxeles, para la fila que comparte con la tarjeta de fuentes: el chip
   * crece a ese alto y su miniatura crece con él (el resto de la caja lo deja sin
   * definir y el chip mide lo que mide su contenido, como siempre).
   */
  alto?: number;
}> = ({ adjunto, onQuitar, tentativo = false, alto }) => {
  const nombre = adjunto.kind === 'image' ? 'Imagen' : adjunto.name;
  const detalle =
    adjunto.kind === 'pdf'
      ? `${adjunto.paginas} pág.${adjunto.tieneTexto ? '' : ' · escaneado'}`
      : adjunto.kind === 'video'
        ? `Video${adjunto.duracionSeg ? ` · ${formatearDuracion(adjunto.duracionSeg)}` : ''}`
      : adjunto.kind === 'planilla'
      ? `${adjunto.filas} fila(s)${adjunto.hojas > 1 ? ` · ${adjunto.hojas} hojas` : ''}`
      : adjunto.kind === 'texto'
        ? formatearPeso(adjunto.bytes)
        : adjunto.kind === 'product'
          ? 'Producto'
          : adjunto.kind === 'model'
            ? palabraDeModeloActual().titulo
            : adjunto.kind === 'referencia'
              ? adjunto.tipo === 'pose'
                ? 'Pose'
                : adjunto.tipo === 'fondo'
                  ? 'Fondo'
                  : 'Estilo'
              : adjunto.kind === 'moodboard'
                ? `Estilo · ${fotosDelMoodboard(adjunto)}`
                : '';
  const miniatura =
    adjunto.kind === 'image'
      ? adjunto.previewUrl
      : adjunto.kind === 'product' ||
          adjunto.kind === 'model' ||
          adjunto.kind === 'referencia' ||
          adjunto.kind === 'moodboard'
        ? adjunto.imageUrl
        : null;

  /**
   * CUADRADO del tamaño de la tarjeta —la foto llenándolo y los textos en una franja
   * abajo— o en fila, como siempre (ADR-2026-09-22-el-chip-alto-apila-la-foto-arriba,
   * ADR-2026-09-22-el-chip-es-un-cuadrado-como-la-tarjeta).
   *
   * Cuadrado cuando el chip mide el alto de la fila de fuentes en ESCRITORIO (92px): ahí
   * la foto se ve de verdad. En el teléfono el chip mide 68 y un cuadrado de ese lado deja
   * la foto en 40px con el nombre encima; sigue en fila. Sin `alto` —el panel lateral, la
   * pantalla del chat, la caja espejo— también en fila: son chips de una conversación, no
   * fichas.
   */
  const cuadrado = !!alto && alto >= 88;

  /** El ícono de cada tipo, para lo que no tiene foto. */
  const icono =
    adjunto.kind === 'model' ? (
      <User className="h-4 w-4 shrink-0 text-[#02062B]/[0.45]" aria-hidden="true" />
    ) : adjunto.kind === 'moodboard' ? (
      <Palette className="h-4 w-4 shrink-0 text-[#02062B]/[0.45]" aria-hidden="true" />
    ) : adjunto.kind === 'referencia' ? (
      adjunto.tipo === 'pose' ? (
        <PersonStanding className="h-4 w-4 shrink-0 text-[#02062B]/[0.45]" aria-hidden="true" />
      ) : adjunto.tipo === 'moodboard' ? (
        <Palette className="h-4 w-4 shrink-0 text-[#02062B]/[0.45]" aria-hidden="true" />
      ) : (
        <ImageIcon className="h-4 w-4 shrink-0 text-[#02062B]/[0.45]" aria-hidden="true" />
      )
    ) : adjunto.kind === 'image' ? (
      <ImageIcon className="h-4 w-4 shrink-0 text-[#02062B]/[0.45]" aria-hidden="true" />
    ) : adjunto.kind === 'planilla' ? (
      <Table2 className="h-4 w-4 shrink-0 text-[#02062B]/[0.45]" aria-hidden="true" />
    ) : adjunto.kind === 'pdf' ? (
      <FileType2 className="h-4 w-4 shrink-0 text-[#02062B]/[0.45]" aria-hidden="true" />
    ) : adjunto.kind === 'video' ? (
      <Film className="h-4 w-4 shrink-0 text-[#02062B]/[0.45]" aria-hidden="true" />
    ) : (
      <FileText className="h-4 w-4 shrink-0 text-[#02062B]/[0.45]" aria-hidden="true" />
    );

  /* En CUADRADO la foto llena el tile entero (`absolute inset-0`) y los textos van encima,
     en una franja al pie: es lo que hace que una imagen de 92×92 se vea como la foto que
     es y no como una miniatura al lado de un rótulo. Lo que no tiene foto —un PDF, una
     planilla— muestra su ícono centrado sobre el gris del chip. En fila es la miniatura de
     siempre: 28px, o el alto útil cuando el chip tiene alto fijo. */
  const laFoto = cuadrado ? (
    <span className="absolute inset-0 flex items-center justify-center bg-[#F0F2F7]">
      {miniatura ? (
        <img src={miniatura} alt="" className="h-full w-full object-cover" />
      ) : (
        icono
      )}
    </span>
  ) : miniatura ? (
    <img
      src={miniatura}
      alt=""
      className={`shrink-0 rounded-lg object-cover ${alto ? '' : 'h-7 w-7'}`}
      style={alto ? { height: alto - 14, width: alto - 14 } : undefined}
    />
  ) : (
    icono
  );

  // 116 y no 140: con la columna del chat en su ancho mínimo con lienzo (480 → 418 útiles
  // para la fila), dos chips tienen que entrar en UNA fila; a 140 el segundo bajaba de
  // renglón (pedido de Matías del 2026-09-16,
  // ADR-2026-09-16-con-el-lienzo-la-biblioteca-espeja-la-caja).
  const losTextos = (
    <span className={`block min-w-0 ${cuadrado ? '' : 'max-w-[116px] shrink-0'}`}>
      <span
        className={`block truncate font-medium text-brand-black ${
          cuadrado ? 'text-[11px] leading-tight' : 'text-[12.5px]'
        }`}
      >
        {nombre}
      </span>
      {/* En el tile cuadrado el segundo renglón va SIEMPRE, aunque el adjunto no tenga
          tipo que decir (una imagen suelta, por ejemplo): si no, la franja de ese tile
          queda más baja que la de los demás y la fila se ve despareja
          (ADR-2026-09-22-la-franja-del-tile-mide-siempre-lo-mismo). Vacío lleva un espacio
          duro —no un texto inventado— y así los nombres de todos los tiles caen en la
          misma línea. En fila (el chat) sigue apareciendo sólo si hay algo que decir: allá
          el chip mide lo que mide su contenido. */}
      {cuadrado ? (
        <span className="block truncate text-[10px] leading-tight text-[#02062B]/[0.5]">
          {detalle || '\u00A0'}
        </span>
      ) : (
        detalle && <span className="block truncate text-[11px] text-[#02062B]/[0.5]">{detalle}</span>
      )}
    </span>
  );

  const elBotonDeQuitar = (
    <button
      type="button"
      onClick={onQuitar}
      aria-label={`Quitar ${nombre}`}
      /* En el cuadrado no hay lugar al costado: va arriba a la derecha, SOBRE la foto, y
         con fondo blanco propio —sobre una imagen cualquiera una X gris no se ve—. Siempre
         visible y no en hover: en el teléfono no hay hover y el chip quedaría sin forma de
         sacarse. */
      className={
        cuadrado
          ? 'absolute right-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/85 text-[#02062B]/[0.6] shadow-sm transition-colors hover:bg-white hover:text-brand-black'
          : 'ml-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[#02062B]/[0.45] transition-colors hover:bg-[#02062B]/[0.08] hover:text-brand-black'
      }
    >
      <X className="h-3.5 w-3.5" />
    </button>
  );

  return (
    <span
      // El id va en el DOM para que la miniatura que vuela desde la biblioteca sepa
      // dónde aterrizar (ADR-2026-09-12-la-miniatura-vuela-hasta-la-caja).
      {...{ [ATRIBUTO_CHIP]: adjunto.id }}
      title={
        tentativo
          ? 'Seleccionada en el lienzo: escribí para adjuntarla'
          : detalle
            ? `${nombre} · ${detalle}`
            : nombre
      }
      aria-label={tentativo ? `${nombre}, seleccionada en el lienzo: escribí para adjuntarla` : undefined}
      className={`group relative rounded-xl border border-[#02062B]/[0.08] bg-[#F0F2F7] transition-opacity ${
        tentativo ? 'opacity-40' : ''
      } ${
        cuadrado
          ? // `shrink-0 grow-0`: mide el LADO de la tarjeta y no se estira a lo que sobre
            // de la fila. Sin esto, un chip solo en su renglón se abría a todo el ancho.
            'block shrink-0 grow-0 overflow-hidden'
          : 'inline-flex shrink-0 items-center gap-2 py-1.5 px-2.5'
      }`}
      style={alto ? (cuadrado ? { height: alto, width: alto } : { height: alto }) : undefined}
    >
      {laFoto}
      {cuadrado ? (
        /* La franja del pie: nombre y tipo sobre la foto, en blanco casi opaco para que se
           lean igual sobre una imagen clara o oscura. `backdrop-blur` para los bordes con
           mucho detalle (una foto de comida, una textura). */
        <span className="absolute inset-x-0 bottom-0 bg-white/90 px-2 py-1.5 backdrop-blur-[2px]">
          {losTextos}
        </span>
      ) : (
        losTextos
      )}
      {!tentativo && elBotonDeQuitar}
    </span>
  );
};

/** El chip del lote: uno solo por más fotos que tenga. */
export const ChipLote: React.FC<{
  lote: LoteEnganchado;
  onQuitar: () => void;
  /** Igual que en `ChipAdjunto`: el alto de la fila de fuentes, cuando la comparte. En
   *  escritorio eso lo vuelve un cuadrado del lado de la tarjeta. */
  alto?: number;
}> = ({ lote, onQuitar, alto }) => {
  const cuadrado = !!alto && alto >= 88;
  const cuantas = `${lote.cantidad} ${lote.cantidad === 1 ? 'foto' : 'fotos'}`;
  return (
    <span
      title={`${cuantas} · Para lo que le pidas`}
      className={`group relative rounded-xl border border-[#1644EB]/[0.25] bg-[#1644EB]/[0.06] ${
        cuadrado
          ? 'block shrink-0 grow-0 overflow-hidden'
          : 'inline-flex shrink-0 items-center gap-2 py-1.5 px-2.5'
      }`}
      style={alto ? (cuadrado ? { height: alto, width: alto } : { height: alto }) : undefined}
    >
      {cuadrado ? (
        <>
          {/* Sin miniatura: el lote son muchas fotos y ninguna lo representa. El ícono
              grande, centrado, sobre el azul clarito del chip. */}
          <span className="absolute inset-0 flex items-center justify-center">
            <Package className="h-7 w-7 text-brand-primary/70" aria-hidden="true" />
          </span>
          <span className="absolute inset-x-0 bottom-0 bg-white/90 px-2 py-1.5 backdrop-blur-[2px]">
            <span className="block truncate text-[11px] font-medium leading-tight text-brand-black">
              {cuantas}
            </span>
            <span className="block truncate text-[10px] leading-tight text-[#02062B]/[0.5]">
              Para lo que le pidas
            </span>
          </span>
        </>
      ) : (
        <>
          <Package className="h-4 w-4 shrink-0 text-brand-primary" aria-hidden="true" />
          <span className="min-w-0">
            <span className="block text-[12.5px] font-medium text-brand-black">{cuantas}</span>
            <span className="block text-[11px] text-[#02062B]/[0.5]">Para lo que le pidas</span>
          </span>
        </>
      )}
      <button
        type="button"
        onClick={onQuitar}
        aria-label="Quitar las fotos"
        className={
          cuadrado
            ? 'absolute right-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/85 text-[#02062B]/[0.6] shadow-sm transition-colors hover:bg-white hover:text-brand-black'
            : 'ml-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[#02062B]/[0.45] transition-colors hover:bg-[#02062B]/[0.08] hover:text-brand-black'
        }
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </span>
  );
};

export const BarraEntradaChat: React.FC<Props> = ({
  valor,
  onCambiarValor,
  onEnviar,
  streaming,
  onDetener,
  cola,
  onEncolar,
  onQuitarDeCola,
  interruptores,
  onCambiarInterruptor,
  preferencias,
  onCambiarPreferencia,
  adjuntos,
  onAgregarAdjunto,
  onQuitarAdjunto,
  ocultarChip,
  adjuntosTentativos = [],
  loteFotos = null,
  onAdjuntarLote,
  onQuitarLote,
  onAbrirSelector,
  companyId,
  inteligencia,
  onCambiarInteligencia,
  puedeSubirInteligencia,
  modeloDeImagen,
  onCambiarModeloDeImagen,
  autoFoco,
  // La segunda frase enseña la `@` (ADR-2026-09-21-la-arroba-menciona-los-adjuntos): sin
  // ella nadie la descubre. Dice "que agregaste" porque menciona los adjuntos, no cualquier
  // imagen de la cuenta.
  placeholder = 'Preguntále lo que quieras a Atlas… Usá @ para mencionar una imagen que agregaste.',
  alto = 'normal',
  rotuloAgregar = false,
  conBotonMas = true,
  onAbrirFuentes,
  variante = 'pantalla',
  onEnfoque,
  pedidoDeFoco,
  encabezado,
  tarjetaDeFuentes,
  pastillasDeContenido = false,
}) => {
  const [menuAbierto, setMenuAbierto] = useState(false);
  // Con el foco en el textarea el trazo gris pasa a ser el aro animado.
  const [enfocado, setEnfocado] = useState(false);
  // Dictado por voz: mientras está activo, el textarea + la fila de controles se
  // reemplazan por <DictadoVoz>, igual que en "Crear desde cero".
  const [dictando, setDictando] = useState(false);
  const [cargandoAdjunto, setCargandoAdjunto] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [avisoSuave, setAvisoSuave] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // La caja: el menú del "+" (portaleado) toma su ancho y se apoya en su borde.
  const cajaRef = useRef<HTMLDivElement | null>(null);

  // ── Los menús del `#` (colores) y la `@` (menciones), compartidos con la caja de Redes
  //    (ADR-2026-09-21-el-numeral-abre-colores, ADR-2026-09-21-la-arroba-menciona-los-adjuntos,
  //    ADR-2026-10-04-numeral-y-arroba-en-redes).
  const menusDeTexto = useMenusDeTexto({ valor, onCambiarValor, adjuntos, textareaRef });
  const archivoRef = useRef<HTMLInputElement | null>(null);
  const masRef = useRef<HTMLButtonElement | null>(null);

  // Auto-resize del textarea, con tope: pasado el tope scrollea adentro en vez de
  // comerse la conversación. El tope es el MISMO que el composer de "Crear desde cero"
  // (240px ≈ 11 renglones): con 160 un pedido de tres frases ya scrolleaba adentro y no
  // se podía releer entero antes de mandarlo
  // (ADR-2026-09-08-caja-de-prompt-y-voz-en-el-chat).
  //
  // Se mide en `useLayoutEffect` (antes de pintar, sin salto) y se vuelve a medir cuando
  // cambia el ANCHO del textarea, no sólo el texto: el panel del chat termina su animación
  // de apertura, se abre la sidebar, se restaura el borrador antes de que el layout asiente
  // o se achica la ventana, y el texto se reacomoda en más renglones con el alto viejo —
  // dos líneas y barra de scroll (ADR-2026-09-13-la-caja-recalcula-el-alto-al-cambiar-de-ancho).
  // El observer mira sólo el ancho a propósito: cambiar el alto lo dispararía a él mismo.
  // Y mientras el texto entra en el tope, el scroll interno va oculto: con dos renglones
  // justos, un redondeo de subpíxel alcanzaba para que apareciera la barra.
  //
  // En `amplio` (el chat vacío a pantalla completa) el textarea es `flex-1` dentro de una
  // caja de 200px mínimo: ahí `height` NO manda —el flex reparte lo que sobra después de
  // los chips y los botones— y con tres filas de chips le quedaban dos renglones para un
  // texto de cinco. Por eso en ese modo se fija `min-height` con el alto del contenido:
  // nunca menos que el texto, y el flex sigue llenando el resto cuando la caja es más
  // alta (ADR-2026-09-13-la-caja-recalcula-el-alto-al-cambiar-de-ancho-2).
  const ajustarAlto = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.minHeight = '';
    const contenido = el.scrollHeight;
    const medido = Math.min(contenido, ALTO_MAX_TEXTAREA);
    if (alto === 'amplio') {
      el.style.height = '';
      el.style.minHeight = `${medido}px`;
    } else {
      el.style.height = `${medido}px`;
    }
    el.style.overflowY = contenido > ALTO_MAX_TEXTAREA ? 'auto' : 'hidden';
  };
  useLayoutEffect(ajustarAlto, [valor, dictando, alto]);
  useEffect(() => {
    const el = textareaRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let ancho = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === ancho) return;
      ancho = el.clientWidth;
      ajustarAlto();
    });
    ro.observe(el);
    return () => ro.disconnect();
    // `ajustarAlto` lee refs y no cambia de significado: no va en deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (autoFoco) textareaRef.current?.focus();
  }, [autoFoco]);

  // El ancho del CONTENIDO de la fila de abajo (sin su `px-2`), sólo cuando el rótulo de
  // "Agregar" va si entra. En un layout effect para que el primer pintado ya salga bien.
  // La fila ocupa el ancho de la caja: lo que se muestra adentro no cambia lo que mide,
  // así que no hay ida y vuelta.
  const filaRef = useRef<HTMLDivElement | null>(null);
  const [anchoFila, setAnchoFila] = useState<number | null>(null);
  const medirFila = rotuloAgregar === 'si-entra';
  useLayoutEffect(() => {
    const el = filaRef.current;
    if (!medirFila || !el) return;
    const medir = () => setAnchoFila(el.clientWidth - 16);
    medir();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [medirFila]);
  const disposicion = disposicionDeLaFila(medirFila ? anchoFila : null);
  const conRotuloAgregar = rotuloAgregar === true || (medirFila && disposicion.conRotulo);
  const pastillasCompactas = medirFila && disposicion.compactas;

  // El pedido de foco va en `useLayoutEffect` y no en `useEffect`: tiene que correr
  // dentro del mismo gesto del click en la sugerencia, porque iOS sólo abre el teclado
  // por un `focus()` que venga de un toque del usuario. El texto completado ya está en
  // el DOM (llega en el mismo render que el contador), así que el cursor va al final.
  useLayoutEffect(() => {
    if (!pedidoDeFoco) return;
    const el = textareaRef.current;
    if (!el) return;
    el.focus();
    const fin = el.value.length;
    el.setSelectionRange(fin, fin);
  }, [pedidoDeFoco]);

  const hayTexto = valor.trim().length > 0;
  // Con un lote enganchado se puede mandar sin escribir: el pedido es obvio.
  const puedeEnviar = (hayTexto || !!loteFotos) && !streaming && !cargandoAdjunto;
  // El mic ya no se turna con el botón de enviar: convive con él, con la caja vacía o a
  // medio escribir (dictar es otra forma de escribir). Firefox no tiene Web Speech API:
  // ahí no se muestra (ADR-2026-09-08-caja-de-prompt-y-voz-en-el-chat).
  const mostrarMic = !cargandoAdjunto && dictadoDisponible();

  // Con el agente respondiendo, Enviar ENCOLA (ADR-2026-09-13-la-cola-de-mensajes-del-
  // agente): el texto sube a la franja de arriba de la caja y sale cuando termine.
  // Las MISMAS condiciones que enviar: un lote de fotos sin texto también se puede
  // encolar, con el pedido por defecto (ADR-2026-09-13-la-cola-lleva-sus-adjuntos).
  const puedeEncolar =
    streaming && (hayTexto || !!loteFotos) && !cargandoAdjunto && hayLugarEnCola(cola);
  // Un color que sale en un mensaje (elegido del menú, escrito o pegado) pasa a "Últimos
  // usados" al mandarlo o encolarlo.
  const recordarColoresDelMensaje = () => menusDeTexto.recordarColoresDe(valor);
  const enviar = () => {
    if (streaming) {
      if ((!hayTexto && !loteFotos) || cargandoAdjunto) return;
      if (!hayLugarEnCola(cola)) {
        setAviso(`La cola admite hasta ${MAX_COLA} mensajes. Esperá a que salga alguno.`);
        return;
      }
      if (onEncolar(hayTexto ? valor : PEDIDO_POR_DEFECTO_LOTE)) {
        setAviso(null);
        recordarColoresDelMensaje();
      }
      return;
    }
    if (!puedeEnviar) return;
    recordarColoresDelMensaje();
    onEnviar(hayTexto ? valor : PEDIDO_POR_DEFECTO_LOTE);
    setAviso(null);
    setAvisoSuave(null);
  };

  const elegirAccion = (accion: AccionMenuMas) => {
    setAviso(null);
    if (accion === 'archivos') archivoRef.current?.click();
    else onAbrirSelector?.(accion);
  };

  const engancharLote = (archivos: File[]) => {
    if (!onAdjuntarLote) return;
    onAdjuntarLote(archivos.slice(0, MAX_FOTOS_LOTE));
    setAvisoSuave(mensajeLoteFotos(archivos.length));
  };

  // UN solo camino para los archivos, vengan del `<input type=file>` del "+" o de un
  // pegado sobre la caja (ADR-2026-09-13-pegar-archivos-en-la-caja-del-agente): las
  // mismas reglas de lote, tope, formato y avisos. Si mañana se suma arrastrar y soltar,
  // entra por acá también.
  const engancharArchivos = async (elegidos: File[]) => {
    if (elegidos.length === 0) return;

    // Muchas imágenes de una NO viajan en el mensaje: quedan disponibles y el agente las
    // mira de a diez con `ver_fotos` (ADR-2026-09-08-el-agente-mira-las-fotos-de-a-diez).
    // Qué se hace con ellas lo decide la instrucción, no la cantidad.
    if (onAdjuntarLote && esLoteDeFotos(elegidos)) {
      engancharLote(elegidos);
      return;
    }

    const libre = lugarLibreAdjuntos(archivosEnganchados(adjuntos));
    if (libre === 0) {
      setAviso(mensajeLimiteAdjuntos());
      return;
    }
    const aProcesar = elegidos.slice(0, libre);
    const sobraron = elegidos.length > libre;

    setCargandoAdjunto(true);
    let fallo: string | null = null;
    for (const archivo of aProcesar) {
      try {
        onAgregarAdjunto(await prepararAdjunto(archivo, companyId));
      } catch (err) {
        // Se guarda el PRIMER motivo y se sigue con el resto: que un archivo no sirva
        // no tiene por qué tirar abajo a los otros.
        fallo = fallo ?? (err instanceof Error ? err.message : 'No pude leer ese archivo.');
      }
    }
    setCargandoAdjunto(false);
    setAviso(fallo ?? (sobraron ? mensajeLimiteAdjuntos() : null));
  };

  const alElegir = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const elegidos = Array.from(ev.target.files ?? []);
    // El input se limpia YA: sin esto, volver a elegir el mismo archivo no dispara
    // `change` (el valor no cambió) y el usuario cree que se rompió.
    ev.target.value = '';
    void engancharArchivos(elegidos);
  };

  // Pegar sobre la caja: si el portapapeles trae archivos (una captura, una imagen
  // copiada de una web, archivos del Finder), se enganchan como si se hubieran elegido
  // con el "+" y el pegado NO escribe nada en la caja (`preventDefault`: sin eso algunos
  // navegadores pegan los nombres de los archivos como texto). Sin archivos no se toca
  // nada y el texto entra como siempre.
  const alPegar = (ev: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pegados = archivosDelPortapapeles(ev.clipboardData);
    if (pegados.length === 0) return;
    ev.preventDefault();
    setAviso(null);
    void engancharArchivos(pegados);
  };

  // Arrastrar y soltar sobre la caja entera (ADR-2026-09-13-arrastrar-archivos-a-la-caja-
  // del-agente). Un contador y no un booleano: al pasar por encima de los chips o del
  // textarea el navegador dispara `dragleave` del padre y `dragenter` del hijo, y con un
  // booleano el "soltá acá" parpadea. Sólo reacciona a ARCHIVOS: arrastrar texto o un
  // link no prende nada y sigue haciendo lo de siempre.
  const [arrastrando, setArrastrando] = useState(false);
  const arrastresRef = useRef(0);
  const alEntrarArrastre = (ev: React.DragEvent<HTMLDivElement>) => {
    if (!traeArchivos(ev.dataTransfer)) return;
    ev.preventDefault();
    arrastresRef.current += 1;
    setArrastrando(true);
  };
  const alSalirArrastre = (ev: React.DragEvent<HTMLDivElement>) => {
    if (!traeArchivos(ev.dataTransfer)) return;
    arrastresRef.current = Math.max(0, arrastresRef.current - 1);
    if (arrastresRef.current === 0) setArrastrando(false);
  };
  const alSobrevolar = (ev: React.DragEvent<HTMLDivElement>) => {
    if (!traeArchivos(ev.dataTransfer)) return;
    // Sin esto el navegador no permite soltar (y en algunos abre el archivo).
    ev.preventDefault();
    ev.dataTransfer.dropEffect = 'copy';
  };
  const alSoltar = (ev: React.DragEvent<HTMLDivElement>) => {
    if (!traeArchivos(ev.dataTransfer)) return;
    ev.preventDefault();
    arrastresRef.current = 0;
    setArrastrando(false);
    const soltados = archivosArrastrados(ev.dataTransfer);
    if (soltados.length === 0) return;
    setAviso(null);
    void engancharArchivos(soltados);
  };

  return (
    <div className="relative">
      <input
        ref={archivoRef}
        type="file"
        multiple
        accept={ACEPTA_ADJUNTOS}
        onChange={alElegir}
        className="hidden"
        tabIndex={-1}
        aria-hidden="true"
      />
      {/* Un solo trazo gris (no borde + ring: se leían como dos líneas). Con el foco, el
          aro de gradiente (`.composer-borde-vivo`) se planta sobre el borde: la caja
          tiene que ser `relative` para que el ::before la envuelva. En `amplio` la caja
          es columna y el textarea se queda con todo el alto que sobra. */}
      {/* EN COLA: la caja se extiende hacia arriba con otro fondo. Misma línea de borde
          que la caja, esquinas de arriba redondeadas, y la caja se le monta encima
          (`-mb-5` + `pb-5`) para que la costura no se vea. */}
      {cola.length > 0 && (
        <div className="rounded-t-3xl border border-b-0 border-neutral-300 bg-[#F0F2F7] px-4 pb-8 pt-3 -mb-5 animate-fade-in">
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#02062B]/[0.45]">
            En cola
          </p>
          <ul className="flex flex-col gap-1">
            {cola.map((m, i) => {
              // Cuántas cosas van con ese mensaje: los chips más el lote, que es UN chip
              // pero muchas fotos (ADR-2026-09-13-la-cola-lleva-sus-adjuntos). Sin este
              // contador, la tira muestra sólo el texto y no hay forma de saber si los
              // adjuntos quedaron enganchados al mensaje o se perdieron.
              const cuantosAdjuntos = m.adjuntos.length + (m.loteFotos ? m.loteFotos.cantidad : 0);
              return (
              <li key={`${i}-${m.texto.slice(0, 24)}`} className="flex items-center gap-2 text-[13px] text-brand-black">
                <Clock className="h-3.5 w-3.5 shrink-0 text-[#02062B]/[0.4]" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate">{m.texto}</span>
                {cuantosAdjuntos > 0 && (
                  <span
                    className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#02062B]/[0.06] px-2 py-0.5 text-[11px] font-medium text-[#02062B]/[0.55]"
                    title={`${cuantosAdjuntos} adjunto${cuantosAdjuntos === 1 ? '' : 's'} en este mensaje`}
                  >
                    <Paperclip className="h-3 w-3" aria-hidden="true" />
                    {cuantosAdjuntos}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => onQuitarDeCola(i)}
                  aria-label={`Sacar de la cola: ${m.texto.slice(0, 40)}`}
                  className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[#02062B]/[0.45] transition-colors hover:bg-[#02062B]/[0.06] hover:text-brand-black"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </li>
              );
            })}
          </ul>
        </div>
      )}
      <div
        ref={cajaRef}
        {...{ [ATRIBUTO_CAJA]: '' }}
        onDragEnter={alEntrarArrastre}
        onDragOver={alSobrevolar}
        onDragLeave={alSalirArrastre}
        onDrop={alSoltar}
        className={`relative rounded-3xl border bg-white p-1 shadow-sm transition-shadow duration-200 ${
          arrastrando ? 'border-dashed border-[#1644EB]' : 'border-neutral-300'
        } ${enfocado && !dictando && !arrastrando ? 'composer-borde-vivo' : ''} ${
          alto === 'amplio' ? 'flex min-h-[200px] flex-col' : ''
        }`}
      >
        {arrastrando && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-3xl bg-[#1644EB]/[0.06] text-[13.5px] font-medium text-[#1644EB]"
          >
            Soltá acá para adjuntar
          </div>
        )}
        {encabezado && !dictando && <div className="px-1.5 pt-1.5">{encabezado}</div>}
        {/* LA TARJETA DE FUENTES Y LO ENGANCHADO, EN LA MISMA FILA —
            ADR-2026-09-22-lo-enganchado-va-al-lado-de-la-tarjeta (ajusta
            ADR-2026-09-22-la-tarjeta-va-arriba-del-texto, que le dio su propia fila).

            La tarjeta a la izquierda, fija, y los chips de lo que se enganchó a su
            DERECHA. Debajo, el chip quedaba solo en un renglón nuevo mientras al lado de
            la tarjeta —que mide 92px de alto— sobraba toda la fila: dos renglones para
            dos cosas que son la misma idea ("de acá sale lo que va a la foto" y "esto es
            lo que pusiste"). Al lado, además, se lee como el resultado de tocar la
            tarjeta.

            Los chips se CENTRAN contra el alto de la tarjeta (`content-center` +
            `self-stretch`): con uno solo queda a media altura y no colgando del techo, y
            cuando son varios y envuelven, el bloque entero sigue centrado.

            Sin tarjeta —el panel lateral y la pantalla del chat— la fila es sólo los
            chips, con el mismo aire de antes.

            Mientras se DICTA la tarjeta no se dibuja: ahí el área de escritura la ocupa el
            dictado y la tarjeta abriría un pop-up encima de una grabación en curso. */}
        {(() => {
          const conTarjeta = !!tarjetaDeFuentes && !dictando;
          const chips = ocultarChip ? adjuntos.filter((a) => !ocultarChip(a)) : adjuntos;
          const hayEnganchado =
            chips.length > 0 || adjuntosTentativos.length > 0 || cargandoAdjunto || !!loteFotos;
          // Con la tarjeta al lado, lo enganchado mide LO MISMO que ella
          // (ADR-2026-09-22-los-chips-miden-lo-mismo-que-la-tarjeta). Sin tarjeta —el panel
          // lateral y la pantalla del chat— los chips miden lo que su contenido, como
          // siempre: allá no hay nada con lo que alinearse.
          const altoDeLosChips = conTarjeta ? ALTO_DE_LA_FILA_DE_FUENTES[variante] : undefined;
          if (!conTarjeta && !hayEnganchado) return null;

          const losChips = (
            <>
              {loteFotos && onQuitarLote && (
                <ChipLote lote={loteFotos} onQuitar={onQuitarLote} alto={altoDeLosChips} />
              )}
              {chips.map((a) => (
                <ChipAdjunto
                  key={a.id}
                  adjunto={a}
                  onQuitar={() => onQuitarAdjunto(a.id)}
                  alto={altoDeLosChips}
                />
              ))}
              {adjuntosTentativos.map((a) => (
                <ChipAdjunto key={`tentativo-${a.id}`} adjunto={a} tentativo alto={altoDeLosChips} />
              ))}
              {cargandoAdjunto && (
                /* El hueco de lo que está subiendo toma la MISMA forma que va a tener
                   cuando llegue: cuadrado en la fila de la tarjeta, pastilla en el chat.
                   Si no, al aparecer la foto la fila se reacomoda de golpe. */
                <span
                  className={`shrink-0 rounded-xl bg-[#F0F2F7] text-[11px] font-medium text-[#02062B]/[0.55] ${
                    altoDeLosChips && altoDeLosChips >= 88
                      ? 'flex grow-0 flex-col items-center justify-center gap-1.5'
                      : 'inline-flex items-center gap-2 py-2 px-3 text-[12.5px]'
                  }`}
                  style={
                    altoDeLosChips
                      ? altoDeLosChips >= 88
                        ? { height: altoDeLosChips, width: altoDeLosChips }
                        : { height: altoDeLosChips }
                      : undefined
                  }
                >
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  Preparando…
                </span>
              )}
            </>
          );

          /**
           * EN EL TELÉFONO, UNA SOLA TIRA QUE SE CORRE —
           * ADR-2026-09-22-en-el-telefono-la-fila-de-fuentes-se-corre.
           *
           * La tarjeta y todo lo enganchado van en el MISMO contenedor, en una sola línea
           * que scrollea a la derecha. Envolviendo, cada cosa se iba a su propio renglón
           * (en 350px no entran dos) y con cuatro adjuntos la caja se comía el hero: la
           * tira deja siempre a la vista la tarjeta y lo primero que se enganchó, y el
           * resto está a un gesto.
           *
           * `px-2` adentro del scroller y no afuera: así el primer tile arranca alineado
           * con el texto de abajo y el último no queda cortado al ras del borde.
           */
          if (conTarjeta && variante === 'panel') {
            return (
              <div className="scrollbar-hide flex items-center gap-2 overflow-x-auto px-2 pt-2 pb-2">
                <div className="shrink-0">{tarjetaDeFuentes}</div>
                {losChips}
              </div>
            );
          }

          return (
            <div
              /* `pb-2` sólo con tarjeta: es lo que la separa de lo que sigue (el texto).
                 Pegada, se leía como el techo del textarea y no como un control aparte;
                 junto con el `pt-2` del textarea son 16px. */
              className={`flex items-start gap-2 ${conTarjeta ? 'px-2 pt-2 pb-2' : 'px-2.5 pt-2.5'}`}
            >
              {conTarjeta && <div className="shrink-0">{tarjetaDeFuentes}</div>}
              {hayEnganchado && (
                <div className="flex min-w-0 flex-1 flex-wrap content-center items-center gap-2 self-stretch">
                  {losChips}
                </div>
              )}
            </div>
          );
        })()}

        {dictando ? (
          <DictadoVoz
            onCancel={() => setDictando(false)}
            onConfirm={(t) => {
              setDictando(false);
              // SUMA, no pisa: el mic está también con la caja a medio escribir, y
              // reemplazar borraría lo tipeado sin que el usuario lo vea.
              const previo = valor.trim();
              onCambiarValor(previo ? `${previo} ${t}` : t);
              // Foco al textarea ya remontado, con lo dictado listo para retocar.
              window.setTimeout(() => textareaRef.current?.focus(), 0);
            }}
          />
        ) : (
          <>
        {/* El textarea y, detrás, la capa espejo que resalta `#colores` y `@menciones`
            (ADR-2026-10-04-colores-y-menciones-resaltados-en-la-caja): misma tipografía y
            padding (`CLASES_DEL_TEXTO`), el textarea con el texto transparente y el cursor
            propio. En `amplio` el envoltorio toma el `flex-1` que tenía el textarea. */}
        <div className={`relative ${alto === 'amplio' ? 'flex flex-1 flex-col' : ''}`}>
        {menusDeTexto.resaltado(`${CLASES_DEL_TEXTO} ${tarjetaDeFuentes ? 'pt-2' : 'pt-4'}`)}
        <textarea
          ref={textareaRef}
          value={valor}
          onScroll={menusDeTexto.alScrollear}
          onChange={(e) => {
            onCambiarValor(e.target.value);
            menusDeTexto.releerToken(e.target);
          }}
          onSelect={(e) => menusDeTexto.releerToken(e.currentTarget)}
          onPaste={alPegar}
          onKeyDown={(e) => {
            // Con un menú (`#` o `@`) abierto, las teclas son suyas: flechas, Enter/Tab, Escape.
            if (menusDeTexto.alTeclear(e)) return;
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              enviar();
            }
          }}
          onFocus={() => {
            setEnfocado(true);
            onEnfoque?.(true);
          }}
          onBlur={(e) => {
            setEnfocado(false);
            onEnfoque?.(false);
            menusDeTexto.alDesenfocar(e);
          }}
          placeholder={loteFotos ? 'Contame qué hacer con las fotos, o mandá directo' : placeholder}
          // Dos renglones de alto por defecto: la caja se lee como un lugar para escribir
          // un pedido, no como un buscador. Sigue creciendo con el texto hasta el tope.
          // En `amplio` el alto lo da la caja (200px) y `rows` no manda.
          rows={2}
          // Con la tarjeta arriba, el aire de arriba ya lo da ella: el texto arranca
          // pegado a su fila (`pt-2`) en vez de los 16px que necesita cuando es lo
          // primero de la caja.
          className={`relative w-full resize-none bg-transparent text-transparent caret-[#02062B] outline-none ${CLASES_DEL_TEXTO} ${
            tarjetaDeFuentes ? 'pt-2' : 'pt-4'
          } ${alto === 'amplio' ? 'flex-1' : ''}`}
        />
        </div>

        {menusDeTexto.menus}

        {/* OJO: NADA de `overflow-*` en esta fila — contiene el popover de Inteligencia
            y un overflow lo recortaría (el del "+" y el de las pastillas van por portal y
            no dependen de esto).

            `items-end` y no `items-center`: acá conviven las pastillas (28px, y pueden
            envolver a dos renglones) con el mic y el enviar (36px). Alineados por abajo,
            los botones quedan siempre contra el último renglón de pastillas; centrados,
            flotaban en el medio de un bloque que cambia de alto. */}
        <div ref={filaRef} className="flex items-end gap-2 px-2 pb-2 pt-2">
          {conBotonMas && (
          <button
            ref={masRef}
            type="button"
            aria-label={onAbrirFuentes ? 'Agregar' : 'Más opciones'}
            aria-haspopup={onAbrirFuentes ? 'dialog' : 'menu'}
            aria-expanded={onAbrirFuentes ? undefined : menuAbierto}
            onClick={() => (onAbrirFuentes ? onAbrirFuentes() : setMenuAbierto((v) => !v))}
            onKeyDown={(e) => {
              if (onAbrirFuentes) return;
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
                setMenuAbierto(true);
              }
            }}
            className={`relative inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-full transition-colors ${
              conRotuloAgregar ? 'px-3.5' : 'w-9'
            } ${
              menuAbierto
                ? 'bg-neutral-200 text-brand-black'
                : 'bg-neutral-100 text-[#02062B]/[0.7] hover:bg-neutral-200'
            }`}
          >
            <Plus className="h-4 w-4 shrink-0" strokeWidth={2.25} />
            {conRotuloAgregar && (
              <span className="whitespace-nowrap text-[13.5px] font-medium">Agregar</span>
            )}
            {/* Investigación prendida es lo único de este menú que sale plata de verdad
                (scrapers). Con el menú cerrado no habría forma de saberlo: el punto es
                esa señal. Decorativo — el estado real lo dice `aria-checked` adentro. */}
            {!pastillasDeContenido && interruptores.investigacion && (
              <span
                aria-hidden="true"
                className="absolute right-0 top-0 h-2.5 w-2.5 rounded-full border-2 border-white bg-brand-primary"
              />
            )}
          </button>
          )}

          {/* Sin pastillas, la Inteligencia sigue siendo este botón al lado del "+"
              (el panel lateral y la pantalla del chat). Con pastillas, subió a la fila de
              arriba con el mismo alto que las otras. */}
          {!pastillasDeContenido && (
            <SelectorInteligencia
              valor={inteligencia}
              onCambiar={onCambiarInteligencia}
              puedeSubir={puedeSubirInteligencia}
            />
          )}

          {/* LAS PASTILLAS, EN EL MISMO RENGLÓN que el mic y el enviar —
              ADR-2026-09-22-el-mic-y-el-enviar-van-a-la-derecha-de-las-pastillas. Estuvieron
              unas horas en un renglón propio arriba de éste; con el "+" y la Inteligencia
              fuera de la caja de la home, abajo quedaban dos botones solos ocupando una
              franja entera de la caja.

              `min-w-0 flex-1`: las pastillas se quedan con todo el ancho que sobra y el
              mic y el enviar con el suyo, así que la fila envuelve (o scrollea, en el
              teléfono) SIN empujarlos. El `min-w-0` es lo que deja encogerse a un hijo
              flex con contenido que no entra — sin eso, la fila desborda la caja. */}
          {pastillasDeContenido && (
            <PastillasDeContenido
              preferencias={preferencias}
              onCambiar={onCambiarPreferencia}
              inteligencia={inteligencia}
              onCambiarInteligencia={onCambiarInteligencia}
              puedeSubirInteligencia={puedeSubirInteligencia}
              className="min-w-0 flex-1"
              compactas={pastillasCompactas}
            />
          )}

          <div className="ml-auto flex shrink-0 items-center gap-2">
            {/* El mic siempre (dictar es otra forma de escribir, también con el agente
                respondiendo). */}
            {mostrarMic && (
              <button
                type="button"
                onClick={() => setDictando(true)}
                aria-label="Dictar por voz"
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 text-[#02062B]/[0.7] transition-colors hover:bg-neutral-200"
              >
                <Mic className="h-4 w-4" strokeWidth={2.25} />
              </button>
            )}
            {/* UN botón que se transforma (ADR-2026-09-13-un-boton-que-se-transforma): al
                mandar, pasa a Detener; si el usuario escribe mientras el agente responde,
                vuelve a ser Enviar (que ENCOLA); encolado —la caja vacía—, otra vez
                Detener. Sin agente respondiendo es Enviar, como siempre. */}
            {streaming && !hayTexto ? (
              <button
                type="button"
                onClick={onDetener}
                aria-label="Detener"
                // Fondo SÓLIDO (`bg-brand-black`, sin `/85`): las clases `brand-*` salen
                // de un `var()` y con modificador de opacidad Tailwind genera CSS
                // inválido — el botón quedaba invisible.
                className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-black text-white shadow-sm transition-opacity duration-200 hover:opacity-90"
              >
                <Square className="h-3 w-3 fill-current" />
              </button>
            ) : (
              <button
                type="button"
                onClick={enviar}
                disabled={streaming ? !puedeEncolar : !puedeEnviar}
                aria-label={streaming ? (hayLugarEnCola(cola) ? 'Poner en cola' : 'La cola está llena') : 'Enviar'}
                title={streaming ? (hayLugarEnCola(cola) ? 'Se manda cuando el agente termine' : `Hasta ${MAX_COLA} en cola`) : undefined}
                // El gradiente de generar, atenuado hasta que haya algo para mandar (una
                // sola capa que se apaga con `opacity`: de un gris sólido al gradiente
                // no se puede animar).
                className={`inline-flex h-9 w-9 items-center justify-center rounded-full text-white shadow-sm transition-opacity duration-200 ${
                  (streaming ? puedeEncolar : puedeEnviar) ? 'opacity-100 hover:opacity-90' : 'cursor-not-allowed opacity-40'
                }`}
                style={{ backgroundImage: GRADIENTE_ENVIAR }}
              >
                <ArrowUp className="h-4 w-4" strokeWidth={2.5} />
              </button>
            )}
          </div>
        </div>
          </>
        )}
      </div>

      {aviso ? (
        <p role="status" className="mt-2 px-1 text-[12.5px] text-[#C33B36]">
          {aviso}
        </p>
      ) : avisoSuave && loteFotos ? (
        <p role="status" className="mt-2 px-1 text-[12.5px] text-[#02062B]/[0.55]">
          {avisoSuave}
        </p>
      ) : null}

      {/* Sin el "+" —o con un "+" que abre el selector de fuentes— no hay quién lo abra:
          no se monta. */}
      {conBotonMas && !onAbrirFuentes && (
      <MenuMas
        abierto={menuAbierto}
        onCerrar={() => setMenuAbierto(false)}
        interruptores={interruptores}
        onCambiarInterruptor={onCambiarInterruptor}
        modeloDeImagen={modeloDeImagen}
        onCambiarModeloDeImagen={onCambiarModeloDeImagen}
        preferencias={preferencias}
        onCambiarPreferencia={onCambiarPreferencia}
        /* Lo que se toca en otro lado no se lista acá: es el mismo estado, y verlo dos
           veces es la forma de que se desincronicen en la cabeza del usuario. Sin
           pastillas —o sea, en el chat— el menú queda entero. */
        vistasOcultas={pastillasDeContenido ? VISTAS_FUERA_DEL_MENU : undefined}
        onAccion={elegirAccion}
        disparadorRef={masRef}
        anclaRef={cajaRef}
        columnas={variante === 'panel' ? 2 : 3}
      />
      )}
    </div>
  );
};

export default BarraEntradaChat;
